/**
 * Collection Lazy Loading Handler
 * Handles lazy loading of products in collection grids
 * Re-initializes after filtering/sorting
 */

(function() {
  'use strict';
  
  console.log('[Collection Lazy Load] Script loaded');
  
  // Store active scroll handlers to clean them up
  var activeScrollHandlers = [];
  
  /**
   * Initialize lazy loading for a section
   */
  function initLazyLoadForSection(sectionId) {
    console.log('[Collection Lazy Load] Initializing for section:', sectionId);
    
    var loadButton = document.getElementById('load-more-' + sectionId);
    var productGrid = document.getElementById('product-grid');
    var template = document.getElementById('remaining-products-' + sectionId);
    
    if (!loadButton || !productGrid || !template) {
      console.log('[Collection Lazy Load] Missing elements for section:', sectionId, {
        loadButton: !!loadButton,
        productGrid: !!productGrid,
        template: !!template
      });
      return false;
    }
    
    // Check if already initialized
    if (loadButton.dataset.lazyLoadInitialized === 'true') {
      console.log('[Collection Lazy Load] Already initialized for section:', sectionId);
      return true;
    }
    
    console.log('[Collection Lazy Load] Setting up event handlers for section:', sectionId);
    
    // Get settings from data attributes
    var itemsPerRow = parseInt(loadButton.dataset.itemsPerRow) || 4;
    var rowsPerLoad = parseInt(loadButton.dataset.rowsPerLoad) || 2;
    var autoLoad = loadButton.dataset.autoLoad === 'true';

    // initAllLazyLoadSections runs more than once on the same button (DOM
    // ready, then window load). Drop the previous run's listeners so one click
    // never reveals or fetches twice.
    if (typeof loadButton._lazyLoadTeardown === 'function') {
      loadButton._lazyLoadTeardown();
    }

    // Kept on the button rather than in this closure so an earlier run's
    // fetch still counts.
    var isFetching = function() {
      return loadButton.getAttribute('aria-busy') === 'true';
    };

    // Collections render 250 products per page (Shopify's maximum). Larger
    // collections have further pages, which are fetched through the Section
    // Rendering API as soon as the page loads (see prefetchRemaining), so the
    // shopper still sees one continuous grid with in-stock products first. The
    // page links stay in the HTML for crawlers; they are hidden while Load
    // more can do their job.
    var pagination = document.querySelector('[data-pagination-for="' + sectionId + '"]');
    if (pagination && loadButton.dataset.nextUrl) {
      pagination.style.display = 'none';
    }

    var hasMore = function() {
      return !!template.content.firstElementChild || !!loadButton.dataset.nextUrl;
    };

    var finish = function() {
      console.log('[Collection Lazy Load] All products loaded, hiding button');
      loadButton.parentNode.style.display = 'none';
      cleanupScrollHandlers();
    };

    // Move the next chunk of products from the template into the grid.
    var revealChunk = function() {
      var content = template.content;
      var itemsToLoad = itemsPerRow * rowsPerLoad;
      var count = 0;
      var fragment = document.createDocumentFragment();
      while (count < itemsToLoad && content.firstElementChild) {
        fragment.appendChild(content.firstElementChild);
        count++;
      }
      productGrid.appendChild(fragment);
      console.log('[Collection Lazy Load] Loaded', count, 'items');
      if (!hasMore()) {
        finish();
      }
    };

    // Queue a fetched tile. Sold-out tiles go to the back. In-stock tiles go
    // ahead of the first sold-out tile still waiting in the template or, if
    // every sold-out tile is already showing, ahead of the first one in the
    // grid. Each page is only sorted within itself, so without this a later
    // page's in-stock products would follow this page's sold-out ones.
    var queueItem = function(item) {
      var content = template.content;
      if (item.hasAttribute('data-sold-out')) {
        content.appendChild(item);
        return;
      }
      var firstSoldOut = content.querySelector('[data-sold-out]');
      if (firstSoldOut) {
        content.insertBefore(item, firstSoldOut);
        return;
      }
      var shownSoldOut = productGrid.querySelector(':scope > [data-sold-out]');
      if (shownSoldOut) {
        productGrid.insertBefore(item, shownSoldOut);
        return;
      }
      content.appendChild(item);
    };

    // Fetch the next page's products into the template. Resolves true when
    // products were added.
    var fetchNextPage = function() {
      var nextUrl = loadButton.dataset.nextUrl;
      if (!nextUrl || isFetching()) {
        return Promise.resolve(false);
      }
      loadButton.setAttribute('aria-busy', 'true');
      var url = nextUrl + (nextUrl.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
      console.log('[Collection Lazy Load] Fetching next page:', url);

      return fetch(url, { credentials: 'same-origin' })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('HTTP ' + response.status);
          }
          return response.text();
        })
        .then(function(html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.getElementById('product-grid');
          var nextTemplate = doc.getElementById('remaining-products-' + sectionId);
          var nextButton = doc.getElementById('load-more-' + sectionId);
          var added = 0;

          [nextGrid ? nextGrid.children : [], nextTemplate ? nextTemplate.content.children : []].forEach(function(items) {
            Array.prototype.slice.call(items).forEach(function(item) {
              queueItem(document.importNode(item, true));
              added++;
            });
          });

          if (nextButton && nextButton.dataset.nextUrl) {
            loadButton.dataset.nextUrl = nextButton.dataset.nextUrl;
          } else {
            delete loadButton.dataset.nextUrl;
          }
          console.log('[Collection Lazy Load] Fetched', added, 'products');
          return added > 0;
        })
        .catch(function(error) {
          // Fall back to the ordinary page links rather than dead-ending.
          console.warn('[Collection Lazy Load] Could not fetch the next page:', error);
          delete loadButton.dataset.nextUrl;
          if (pagination) {
            pagination.style.display = '';
          }
          return false;
        })
        .then(function(added) {
          loadButton.removeAttribute('aria-busy');
          return added;
        });
    };

    // Fetch every remaining page now rather than on demand, so in-stock
    // products from later pages are in place before the shopper reaches the
    // sold-out ones. Only collections over 250 products have a next page.
    var prefetchRemaining = function() {
      if (!loadButton.dataset.nextUrl || isFetching()) {
        return;
      }
      fetchNextPage().then(function(added) {
        // An in-stock-only collection can have nothing in stock on page 1;
        // show the first fetched products rather than an empty grid.
        if (added && !productGrid.firstElementChild) {
          revealChunk();
        }
        if (added) {
          prefetchRemaining();
        }
      });
    };

    // Click handler for load more button
    var clickHandler = function(e) {
      e.preventDefault();
      console.log('[Collection Lazy Load] Load button clicked');

      if (template.content.firstElementChild) {
        revealChunk();
        return;
      }

      if (loadButton.dataset.nextUrl) {
        fetchNextPage().then(function(added) {
          if (added) {
            revealChunk();
          } else if (!hasMore()) {
            finish();
          }
        });
        return;
      }

      finish();
    };
    
    loadButton.addEventListener('click', clickHandler);
    
    // Touch event for mobile
    var touchHandler = function(e) {
      e.preventDefault();
      loadButton.click();
    };
    loadButton.addEventListener('touchend', touchHandler);

    loadButton._lazyLoadTeardown = function() {
      loadButton.removeEventListener('click', clickHandler);
      loadButton.removeEventListener('touchend', touchHandler);
    };
    
    // Auto-load on scroll if enabled
    if (autoLoad) {
      console.log('[Collection Lazy Load] Auto-load on scroll enabled');
      
      var scrollHandler = function() {
        if (!hasMore()) {
          console.log('[Collection Lazy Load] No more products, removing scroll handler');
          cleanupScrollHandlers();
          return;
        }
        if (isFetching()) {
          return;
        }

        var rect = loadButton.getBoundingClientRect();
        if (rect.top < window.innerHeight + 300) {
          console.log('[Collection Lazy Load] Button in view, auto-loading');
          loadButton.click();
        }
      };
      
      // Store handlers for cleanup
      activeScrollHandlers.push({
        handler: scrollHandler,
        sectionId: sectionId
      });
      
      window.addEventListener('scroll', scrollHandler, { passive: true });
      document.addEventListener('scroll', scrollHandler, { passive: true });
    }
    
    prefetchRemaining();

    // Mark as initialized
    loadButton.dataset.lazyLoadInitialized = 'true';
    
    console.log('[Collection Lazy Load] Successfully initialized for section:', sectionId);
    return true;
  }
  
  /**
   * Clean up scroll handlers
   */
  function cleanupScrollHandlers() {
    console.log('[Collection Lazy Load] Cleaning up scroll handlers');
    activeScrollHandlers.forEach(function(item) {
      window.removeEventListener('scroll', item.handler);
      document.removeEventListener('scroll', item.handler);
    });
    activeScrollHandlers = [];
  }
  
  /**
   * Find and initialize all lazy load sections on the page
   */
  function initAllLazyLoadSections() {
    console.log('[Collection Lazy Load] Searching for lazy load sections');
    
    // Clean up old handlers first
    cleanupScrollHandlers();
    
    // Find all load-more buttons
    var loadButtons = document.querySelectorAll('[id^="load-more-"]');
    console.log('[Collection Lazy Load] Found', loadButtons.length, 'load-more buttons');
    
    loadButtons.forEach(function(button) {
      // Extract section ID from button ID (format: load-more-SECTION_ID)
      var buttonId = button.id;
      var sectionId = buttonId.replace('load-more-', '');
      
      // Reset initialization flag to allow re-initialization
      button.dataset.lazyLoadInitialized = 'false';
      
      initLazyLoadForSection(sectionId);
    });
  }
  
  /**
   * Initialize on DOM ready
   */
  function init() {
    console.log('[Collection Lazy Load] Initializing on DOM ready');
    initAllLazyLoadSections();
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize after a short delay on window load (backup)
  window.addEventListener('load', function() {
    setTimeout(initAllLazyLoadSections, 100);
  });
  
  // Listen for content changes (when filters are applied)
  // The Filterable component replaces content in #facets-filterable
  var observeContentChanges = function() {
    var filterableEl = document.getElementById('facets-filterable');
    if (!filterableEl) {
      console.log('[Collection Lazy Load] No filterable element found, will retry');
      return;
    }
    
    console.log('[Collection Lazy Load] Setting up MutationObserver for content changes');
    
    var observer = new MutationObserver(function(mutations) {
      var shouldReinit = false;
      
      mutations.forEach(function(mutation) {
        // Check if product grid or load-more button was added/modified
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              // Check if added node contains load-more button or is the results div
              if (node.id === 'facets-results' || 
                  node.querySelector && node.querySelector('[id^="load-more-"]')) {
                shouldReinit = true;
              }
            }
          });
        }
      });
      
      if (shouldReinit) {
        console.log('[Collection Lazy Load] Content changed, re-initializing...');
        // Give the DOM a moment to settle after Alpine.js processes
        setTimeout(initAllLazyLoadSections, 500);
      }
    });
    
    observer.observe(filterableEl, {
      childList: true,
      subtree: true
    });
    
    console.log('[Collection Lazy Load] MutationObserver active');
  };
  
  // Set up observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeContentChanges);
  } else {
    observeContentChanges();
  }
  
  // Also listen for the custom event dispatched by the theme
  document.addEventListener('shapes:section:willmutate', function() {
    console.log('[Collection Lazy Load] Section will mutate, preparing to re-initialize');
  });
  
})();

